<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="failedBeneficiaries"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="endBeneficiariesList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template
        v-once
        #cell(SN)="data"
      >
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(loanAmountAccessed)="data">
        {{ toAmount(data.value) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(validationStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(employmentStatus)="data">
        {{ getEmploymentStatusLabel(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              @click="
                onSelectEndBeneficiaryListItem(data.item, $event.target, 'edit')
              "
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t("Modify") }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="
                onSelectEndBeneficiaryListItem(data.item, $event.target, 'view')
              "
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t("View") }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="failedBeneficiariesRules">
        <b-form>
          <b-row class="match-height">
            <!--  User Options -->

            <!-- First names -->
            <b-col md="6">
              <InputField
                id="beneficiary-name"
                v-model="endBeneficiariesFormData.beneficiaryName"
                :label="$t('Beneficiary Name')"
                :placeholder="$t('Enter Beneficiary name')"
                type="text"
                :error-name="$t('Beneficiary Name')"
                rules="required"
                for="beneficiary-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="BVN"
                v-model="endBeneficiariesFormData.bvn"
                :label="$t('BVN')"
                :placeholder="$t('Enter BVN')"
                type="number"
                :max-length="10"
                :error-name="$t('BVN')"
                rules="required|integer|length:11"
                :readonly="isReadonly"
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="NIN"
                v-model="endBeneficiariesFormData.nin"
                :label="$t('NIN')"
                :placeholder="$t('Enter NIN')"
                type="text"
                :error-name="$t('NIN')"
                :max-length="10"
                rules="required|integer|length:11"
                :readonly="isReadonly"
              />
            </b-col>

            <!--Gender -->
            <b-col md="6">
              <SelectField
                id="gender"
                v-model="endBeneficiariesFormData.gender"
                :label="$t('Gender')"
                :placeholder="$t('Select')"
                :options="genderOptions"
                :reduce="(option) => option.value"
                :error-name="$t('Gender')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Gender -->

            <b-col md="6">
              <SelectField
                id="employmentStatus"
                v-model="endBeneficiariesFormData.employmentStatus"
                :label="$t('Employment Status')"
                :options="employmentStatusOptions"
                :reduce="(option) => option.value"
                :error-name="$t('Employment Status')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :placeholder="$t('Select Employment Status')"
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="sector"
                v-model="endBeneficiariesFormData.sector"
                :label="$t('Sector')"
                :options="sectorList"
                :reduce="(option) => option.value"
                :error-name="$t('Sector')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :placeholder="$t('Select Sector')"
              />
            </b-col>

            <b-col md="6">
              <InputNumberField
                id="loanAmountAccessed"
                v-model.number="endBeneficiariesFormData.loanAmountAccessed"
                :label="$t('Loan Amount Accessed')"
                :placeholder="$t('Enter Loan Amount Accessed')"
                type="text"
                :error-name="$t('Loan Amount Accessed')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="partnerCode"
                v-model="endBeneficiariesFormData.partnerCode"
                :label="$t(`Partner's Code`)"
                :placeholder="$t('Enter Partner Code')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="lastProcessedTime"
                v-model="endBeneficiariesFormData.lastProcessedTime"
                :label="$t('Last Processed Time')"
                :placeholder="$t('Enter Last Processed Time')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="retryCount"
                v-model="endBeneficiariesFormData.retryCount"
                :label="$t('Retry Count')"
                :placeholder="$t('Enter Retry Count')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="validationMessage"
                v-model="endBeneficiariesFormData.validationMessage"
                :label="$t('KYC Validation Message')"
                :placeholder="$t('Enter KYC Validation Message')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="validationStatus"
                v-model="endBeneficiariesFormData.validationStatus"
                :label="$t('KYC Validation Status')"
                :placeholder="$t('Enter KYC Validation Status')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{}"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchEndBeneficiaries"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Beneficiary Name -->
        <b-col md="6">
          <InputField
            id="beneficiary-name"
            v-model="filter.beneficiaryName"
            :label="$t('Beneficiary Name')"
            :placeholder="$t('Enter beneficiary name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>

        <!-- BVN -->
        <b-col md="6">
          <InputField
            id="bvn"
            v-model="filter.bvn"
            :label="$t('BVN')"
            :placeholder="$t('Enter BVN')"
            type="number"
          />
        </b-col>
        <!-- BVN -->

        <!-- NIN -->
        <b-col md="6">
          <InputField
            id="bvn"
            v-model="filter.nin"
            :label="$t('NIN')"
            :placeholder="$t('Enter NIN')"
            type="number"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- NIN -->

        <!-- Gender -->
        <b-col md="6">
          <SelectField
            id="gender"
            v-model="filter.gender"
            :label="$t('Gender')"
            :placeholder="$t('Select Gender')"
            :options="genderOptions"
            :reduce="(option) => option.value"
            item="label"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Gender -->

        <!-- Employment Status -->
        <b-col md="6">
          <SelectField
            id="employmentStatus"
            v-model="filter.employmentStatus"
            :label="$t('Employment Status')"
            :placeholder="$t('Select employment status')"
            :options="employmentStatusOptions"
            :reduce="(option) => option.value"
            item="label"
          />
        </b-col>
        <!-- Employment Status -->

        <!-- Sector -->
        <b-col md="6">
          <SelectField
            id="sector"
            v-model="filter.sector"
            :label="$t('Sector')"
            :placeholder="$t('Select Sector')"
            :options="sectorList"
            :reduce="(option) => option.value"
            item="label"
          />
        </b-col>
        <!-- Sector -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BBadge,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { required, minPhoneVal, maxPhoneVal } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import {
  llmsGender,
  accessLevel,
  employmentStatus,
  sectorList,
} from '@/helpers/data'
import Button from '../../components/button/Button.vue'
import { toDateWithYear } from '@/helpers/formatDate'
import { lmsPermissions } from '@/helpers/permissions'
import { formatAmount, toAmount, toKobo } from '@/helpers/formatAmount'
import InputNumberField from '../../forms/InputNumberField.vue'
import getStatusColor from '@/helpers/getStatusColor'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    TablePagination,
    BModal,
    BForm,
    BSpinner,
    BBadge,
    TableEntries,
    SelectField,
    ValidationObserver,
    InputField,
    InputNumberField,
    Button,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      isReadonly: false,
      isEditable: false,
      isFullName: false,
      showSpinner: false,
      sortBy: 'name',
      required,
      toAmount,
      isLoading: false,
      minPhoneVal,
      maxPhoneVal,
      employmentStatusOptions: employmentStatus,
      sectorList,
      modalTitle: 'Create End Beneficiaries',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'partnerCode', sortable: true, label: 'Partner Code' },
        { key: 'beneficiaryName', sortable: true, label: 'Beneficiary Name' },
        { key: 'bvn', sortable: true, label: 'BVN' },
        { key: 'nin', sortable: true, label: 'NIN' },
        { key: 'gender', sortable: true },
        // { key: 'dateOfBirth', sortable: true, label: 'Date of Birth' },
        { key: 'employmentStatus', sortable: true, label: 'Employment Status' },
        {
          key: 'loanAmountAccessed',
          sortable: true,
          label: 'Loan Amount Accessed',
        },
        {
          key: 'validationMessage',
          sortable: true,
          label: 'Validation Message',
        },
        {
          key: 'validationStatus',
          sortable: true,
          label: 'Validation Status',
        },
        // { key: 'sector', sortable: true },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        //   { key: 'status', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      getStatusColor,
      buttonLabel: 'Save',
      endBeneficiariesFormData: {
        id: '',
        beneficiaryName: '',
        bvn: '',
        nin: '',
        gender: '',
        employmentStatus: '',
        sector: '',
        loanAmountAccessed: '',
        partnerCode: '',
        lastProcessedTime: '',
        retryCount: '',
        validationMessage: '',
        validationStatus: '',
      },
      supervisorEmail: '',
      filter: {
        beneficiaryName: '',
        bvn: '',
        nin: '',
        gender: '',
        employmentStatus: '',
        sector: '',

        sortColumn: '',
      },
      endBeneficiariesList: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    accessLevelOptions() {
      return accessLevel.map(item => ({
        label: this.$t(item.label),
        value: item.value,
      }))
    },
    genderOptions() {
      return llmsGender.map(item => ({
        label: this.$t(item.label),
        value: item.value,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchEndBeneficiaries()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchEndBeneficiaries()
    },
  },
  mounted() {
    this.fetchEndBeneficiaries()
  },
  methods: {
    ...mapActions({
      getFailedEndBeneficiariesList: 'llms/getFailedEndBeneficiariesList',
      updateEndBeneficiaries: 'llms/updateEndBeneficiariesAction',
    }),
    getEmploymentStatusLabel(statusValue) {
      if (statusValue) {
        const matchingStatus = this.employmentStatusOptions.find(
          option => option.value.trim().toLowerCase() === statusValue.trim().toLowerCase(),
        )
        return matchingStatus ? matchingStatus.label : null
      }
      return null
    },
    async fetchEndBeneficiaries() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getFailedEndBeneficiariesList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: lmsPermissions.MODIFY_END_BENEFICIARY_UPLOAD,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.endBeneficiariesList = data.data.map(item => ({
            ...item,
            loanAmountAccessed: formatAmount(item.loanAmountAccessed),
          }))
          this.totalCount = data.recordCount
        } else {
          this.endBeneficiariesList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.failedBeneficiariesRules.validate().then(success => {
        if (success) {
          const actionText = 'Are you sure you want to Update this End Beneficiary?'

          this.confirm(this.failedBeneficiariesAction, { text: actionText })
        }
      })
    },
    async failedBeneficiariesAction() {
      try {
        this.isLoading = true

        const formData = {
          id: this.endBeneficiariesFormData.id,
          beneficiaryName: this.endBeneficiariesFormData.beneficiaryName,
          bvn: this.endBeneficiariesFormData.bvn,
          nin: this.endBeneficiariesFormData.nin,
          gender: this.endBeneficiariesFormData.gender,
          dateOfBirth: this.endBeneficiariesFormData.dateOfBirth,
          employmentStatus: this.endBeneficiariesFormData.employmentStatus,
          loanAmountAccessed: toKobo(
            this.endBeneficiariesFormData.loanAmountAccessed,
          ),
          sector: this.endBeneficiariesFormData.sector,
        }

        const response = await this.updateEndBeneficiaries(formData)
        const { status } = response
        if (status === 200) {
          this.fetchEndBeneficiaries()
          this.hideModal('modal-lg')
          const successMessage = this.$t(
            'Your request to Update End beneficiaries was successful',
          )
          this.alertSuccess({ text: successMessage })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSelectEndBeneficiaryListItem(items, activity, val) {
      this.endBeneficiariesFormData = {
        ...items,
      }
      this.isEditable = true
      this.isReadonly = val === 'edit' ? (this.isReadonly = false) : (this.isReadonly = true)
      this.modalTitle = val === 'edit'
        ? this.$t('Edit End Beneficiaries')
        : this.$t('View End Beneficiaries')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    resetModal() {
      this.endBeneficiariesFormData = { ...null }
      this.isLoading = false
      this.isEditable = false
      this.isReadonly = false
      this.buttonLabel = this.$t('Save')
      this.modalTitle = this.$t('Create End Beneficiary')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchEndBeneficiaries()
    },
  },
}
</script>
